export interface XhrUploadParams {
  url: string;
  method: 'POST' | 'PUT';
  body: FormData | File | Blob;
  headers?: Record<string, string>;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

export interface XhrUploadResult {
  status: number;
  body: string;
}

// XMLHttpRequest wrapper. We use XHR rather than fetch because fetch still
// doesn't expose upload progress in browsers (the spec exists; browsers don't
// implement it). The caller decides what to do with the status — we resolve
// on load regardless of HTTP status code and let the caller treat non-2xx as
// an error.
//
// For presigned PUT calls, do NOT pass an Authorization header. The URL itself
// is the credential; adding extra headers will break the signature.
export function xhrUpload(params: XhrUploadParams): Promise<XhrUploadResult> {
  const { url, method, body, headers, onProgress, signal } = params;

  return new Promise<XhrUploadResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open(method, url);

    if (headers) {
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }
    }

    // xhr.upload.onprogress is the SEND-side progress (what we want).
    // xhr.onprogress is the download side; useless for upload UI.
    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      };
    }

    const onAbort = () => {
      xhr.abort();
    };
    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
    const cleanup = () => {
      if (signal) signal.removeEventListener('abort', onAbort);
    };

    xhr.onload = () => {
      cleanup();
      resolve({ status: xhr.status, body: xhr.responseText });
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error('Network error during upload'));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new Error('Upload timed out'));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    xhr.send(body);
  });
}
